import React, { useEffect } from "react";
import { useRoutes } from "react-router-dom";
import "./app.css";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { create, InsertionPoint } from "jss";
import { ThemeProvider } from "styled-components";

import { StyledEngineProvider } from "@mui/styled-engine-sc";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import AdapterMoment from "@mui/lab/AdapterMoment";

import LocalizationProvider from "@mui/lab/LocalizationProvider";
import StylesProvider from "@mui/styles/StylesProvider";
import jssPreset from "@mui/styles/jssPreset";

import "./i18n";
import createTheme from "./theme";
import routes from "./routes";

import useSettings from "./hooks/useSettings";

import config from "devextreme/core/config";
import "devextreme/dist/css/dx.common.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import themes from "devextreme/ui/themes";
import { DevExtremeMap } from "./constants";
import AppSearchBox from "./components/AppSearchBox/AppSearchBox";
import { BarcodeScannerProvider } from "./components/BarcodeScanner";
import { useSessionExpiration } from "./hooks/useSessionExpiration.js";
import MonolithDeploymentAlerts from "./pages/Alerts/MonolithDeploymentAlerts.js";
import {
  MonolithUIProvider,
  MonolithDefaultTheme,
} from "@monolith-forensics/monolith-ui";
import { useQueryClient } from "@tanstack/react-query";
import { userApi } from "@/api";
import { useAuth } from "./contexts/AuthContext";
import TenantSettingsAPI from "./api/tenant-settings";
import {
  CUSTOM_FIELD_CATEGORIES,
  CustomFieldsAPI,
  CustomFieldsQuery,
  FileUploaderProvider,
  FileUploadManager,
} from "./Features";
import usePlatform from "./hooks/usePlatform";
import AcquisitionsAPI from "./api/acquisitions";
import { FeaturesProvider } from "./contexts/FeaturesContext";
import "devextreme/ui/select_box";
import "devextreme/ui/text_area";
import "devextreme/ui/tag_box";

import { THEMES } from "@monolith-forensics/monolith-ui/dist/theme/variants";
config({
  editorStylingMode: "filled",
});

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById(
    "jss-insertion-point"
  ) as InsertionPoint,
});

function App() {
  useSessionExpiration();
  const { isElectron } = usePlatform();
  const { isAuthenticated } = useAuth();
  const content = useRoutes(routes);

  const { theme } = useSettings();
  const muiTheme = createTheme(theme);
  const devMonolithTheme =
    process.env.NODE_ENV === "development"
      ? (muiTheme as unknown as MonolithDefaultTheme)
      : undefined;

  const queryClient = useQueryClient();

  useEffect(() => {
    themes.current(DevExtremeMap[theme as keyof typeof DevExtremeMap]);
  }, [theme]);

  useEffect(() => {
    if (isElectron) {
      window.addEventListener("keydown", (e) => {
        if (e.ctrlKey && e.key === ".") {
          window.electronAPI.openDevTools();
        }
      });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      queryClient.prefetchQuery({
        queryKey: ["users", "list"],
        queryFn: () => userApi.getUsers(),
      });
      queryClient.prefetchQuery({
        queryKey: ["tenant-settings", "list"],
        queryFn: () => TenantSettingsAPI.getTenantSettings(),
      });
      queryClient.prefetchQuery({
        queryKey: ["acquisitions:status"],
        queryFn: () => AcquisitionsAPI.getStatuses(),
      });
      CustomFieldsQuery.prefetchCustomFields();
    }
  }, [isAuthenticated, queryClient]);

  useEffect(() => {
    // iOS Safari/Chrome can report 100vh larger than visible viewport.
    const setViewportHeight = () => {
      const visibleHeight = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        "--app-visible-height",
        `${Math.round(visibleHeight)}px`
      );
    };

    const viewport = window.visualViewport;
    setViewportHeight();
    window.addEventListener("resize", setViewportHeight);
    window.addEventListener("orientationchange", setViewportHeight);
    viewport?.addEventListener("resize", setViewportHeight);
    viewport?.addEventListener("scroll", setViewportHeight);

    return () => {
      window.removeEventListener("resize", setViewportHeight);
      window.removeEventListener("orientationchange", setViewportHeight);
      viewport?.removeEventListener("resize", setViewportHeight);
      viewport?.removeEventListener("scroll", setViewportHeight);
    };
  }, []);

  return (
    <HelmetProvider>
      <Helmet
        titleTemplate="%s | Monolith"
        defaultTitle="Monolith - Digital Forensics Case Management"
      />
      <StylesProvider jss={jss}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <StyledEngineProvider injectFirst>
            <MuiThemeProvider theme={muiTheme}>
              <ThemeProvider theme={muiTheme}>
                <FeaturesProvider>
                  <BarcodeScannerProvider>
                    <FileUploaderProvider>
                      <MonolithUIProvider
                        colorScheme={theme as THEMES}
                        // Dev only: merge in the MUI theme so styled-components
                        // consumers (MUI + monolith-ui) share a compatible shape.
                        theme={devMonolithTheme}
                      >
                        <FileUploadManager />
                        <AppSearchBox>
                          <MonolithDeploymentAlerts />
                          <>{content}</>
                        </AppSearchBox>
                      </MonolithUIProvider>
                    </FileUploaderProvider>
                  </BarcodeScannerProvider>
                </FeaturesProvider>
              </ThemeProvider>
            </MuiThemeProvider>
          </StyledEngineProvider>
        </LocalizationProvider>
      </StylesProvider>
    </HelmetProvider>
  );
}

export default App;
